// Includes interactions with the API
// Responsible for logic such as:
//  - Making requests to API
//  - Parsing raw API responses into something more consumable by the app
//    (e.g. massaging results, parsing yaml)
//  - Translating app state into API query strings

import fetch from "cross-fetch";
import * as yaml from "js-yaml";
import * as queryString from "query-string";
import { StoreState } from "../types";
import { ApiBaseUrl, apiResultLimit } from "./../../config";
import { apiFieldMappings } from "./../../refdata/FilterOptions";
import { filterChoices } from "./../../common/services/Filters";
import { reports } from "./../../refdata/ReportFormat";

const baseUrl = (apiPath: string) => ApiBaseUrl() + apiPath;

export function fetchData(url: string): Promise<any> {
  return (
    fetch(url)
      .then((response: any) => {
        // either throw an error if the response is bad (non-200)...
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        // ...or otherwise return the json response payload as json
        return response.json();
      })
      // massage the response as necessary
      .then((responseObject: any) => {
        return {
          data: responseObject.data.map((record: any) => record.data),
          totals: responseObject.totals,
        };
      })
  );
}

export const buildFilterString = (filters: StoreState.Query["filters"]) => {
  const filterList: any = [];
  // process single-value fields
  apiFieldMappings.singleValues.forEach(field => {
    let filterValue = filters[field.filterName];
    if (filterValue === "CBS" || filterValue === "CBF") {
      filterValue = "CB";
    } else if (filterValue === "SIS" || filterValue === "SIF") {
      filterValue = "SI";
    }
    if (filterValue) {
      filterList.push(`${field.apiField}:${encodeURIComponent(filterValue)}`);
    }
  });

  // multi-select fields
  apiFieldMappings.multiSelect.forEach(field => {
    const filterValue = filters[field.filterName];
    if (filterValue) {
      // don't specify if it is equivalent to select ALL
      if (filterValue.length !== filterChoices[field.filterName].length) {
        if (!filterValue.length) {
          filterValue.push("");
        } // add an empty element to account for "Select All" option. TODO: refactor
        const orStrings = filterValue
          .map((i: string) => `"${encodeURIComponent(i)}"`)
          .join(",");
        filterList.push(`${field.apiField}:(${orStrings})`);
      }
    }
  });
  // range fields
  apiFieldMappings.rangeSelect.forEach(field => {
    const filterValueStart = filters[field.filterNameStart];
    const filterValueEnd = filters[field.filterNameEnd];
    if (filterValueStart && filterValueEnd) {
      filterList.push(
        `${field.apiField}:[${encodeURIComponent(
          filterValueStart,
        )} TO ${encodeURIComponent(filterValueEnd)}]`,
      );
    }
  });
  // Join filters for query string
  return filterList.join(" AND ");
};

export const buildQueryUrl = (apiPath: string, query: StoreState.Query) => {
  // translates StoreState.query into URL string to feed to API
  let params = {};
  const filters = buildFilterString(query.filters);
  const sort_by = query.sort.sortField;
  const sort_order = query.sort.sortOrder;
  const fields = query.fixedDisplayFields.concat(query.displayFields).join(",");
  const limit = apiResultLimit;
  const react = true; // Used to distinguish React API calls
  params = {
    filters,
    fields,
    sort_by,
    sort_order,
    limit,
    react,
  };
  // bring in existing params defined by the report, if present
  if (query.aggReport) {
    const report = reports["failures"][query.aggReport];
    if (report.agg_by) {
      params = { ...params, agg_by: report.agg_by };
    }
    if (report.agg_sum_fields) {
      params = { ...params, agg_sum_fields: report.agg_sum_fields.join(",") };
    }
    if (report.agg_term_fields) {
      params = { ...params, agg_term_fields: report.agg_term_fields.join(",") };
    }
    if (report.subtotal_by) {
      params = { ...params, subtotal_by: report.subtotal_by };
    }
    if (report.total_fields) {
      params = { ...params, total_fields: report.total_fields.join(",") };
    }
  }

  const paramsString = queryString.stringify(params);
  return `${baseUrl(apiPath)}?${paramsString}`;
};

export const buildCsvDowloadUrl = (
  apiPath: string,
  query: StoreState.Query,
) => {
  // TODO: set filename based on current app or query
  return `${buildQueryUrl(
    apiPath,
    query,
  )}&format=csv&download=true&filename=bank-data`;
};

// Grab column metadata (e.g. human-friendly Title) dynamically from the API docs
export function fetchColumnData(docsPath: string): Promise<any> {
  const url = ApiBaseUrl() + docsPath;
  return (
    fetch(url)
      .then((response: any) => {
        // either throw an error if the response is bad (non-200)...
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        // ...or otherwise return the json response payload as json
        return response.text();
      })
      // massage the response as necessary
      .then((responseObject: any) => {
        var yamlResp: any = yaml;
        var resp = yamlResp.safeLoad(responseObject).properties.data.properties;
        return resp;
      })
  );
}
